// src/components/texting/TextNudgeCard.jsx
// "Suggested follow-up" approval card — appears at the top of the buyer lead
// drawer when the lead has gone quiet for N days (Settings → Automated Texts,
// nudge switch). NEVER auto-sends: a human taps Send / Edit / Dismiss.
import React, { useEffect, useState } from "react";
import { Bell, Send } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { updateBuyerLead, getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  canUseTexting,
  getTextingSettings,
  renderTemplate,
  firstNameOf,
  normalizePhoneE164,
  sendText,
} from "../../utils/texting";

const lastActivityAt = (lead) => {
  const acts = Array.isArray(lead.activities) ? lead.activities : [];
  return acts.reduce((max, a) => Math.max(max, a?.at || 0), lead.createdAt || 0);
};

export default function TextNudgeCard({ lead, canManage }) {
  const [{ user }] = useStateValue();
  const [settings, setSettings] = useState(null);
  const [phone, setPhone] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTextingSettings().then(setSettings);
  }, []);

  const last = lastActivityAt(lead);
  const qualifies =
    !!settings?.nudgeEnabled &&
    canManage &&
    canUseTexting(user) &&
    lead.status === "active" &&
    !lead.doNotText &&
    Date.now() - last > (Number(settings?.nudgeDays) || 7) * 86400000 &&
    (lead.textNudgeDismissedAt || 0) < last;

  useEffect(() => {
    if (!qualifies || !lead.contactId) return;
    let alive = true;
    (async () => {
      try {
        const raw = (await getContactsByIds([lead.contactId]))[0];
        if (alive) setPhone(normalizePhoneE164(normalizeContact(raw)?.phone || ""));
      } catch (e) {
        console.error("nudge phone lookup failed:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [qualifies, lead.contactId]);

  useEffect(() => {
    if (settings) {
      setDraft(
        renderTemplate(settings.nudgeTemplate, {
          firstName: firstNameOf(lead.contactName),
        }),
      );
    }
  }, [settings, lead.contactName]);

  if (!qualifies || !phone) return null;

  const days = Math.floor((Date.now() - last) / 86400000);

  const dismiss = () =>
    updateBuyerLead(lead.id, { textNudgeDismissedAt: Date.now() });

  const send = async () => {
    setBusy(true);
    const res = await sendText({
      to: phone,
      body: draft,
      leadId: lead.id,
      contactName: lead.contactName,
      user,
      source: "nudge",
    });
    setBusy(false);
    if (res.ok) {
      toast.success(res.queued ? "Queued — sends after quiet hours" : "Follow-up text sent");
      await dismiss();
    } else {
      toast.error(res.error || "Text failed to send");
    }
  };

  return (
    <div className="mx-5 mt-3 border border-amber-200 bg-amber-50/60 rounded-xl p-3">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-800">
        <Bell size={14} /> Suggested follow-up · no contact in {days} days
      </div>
      {editing ? (
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mt-2 w-full text-sm border border-amber-300 rounded-lg px-3 py-2 bg-white"
        />
      ) : (
        <p className="mt-2 text-sm text-slate-700 bg-white border border-amber-100 rounded-lg px-3 py-2 whitespace-pre-wrap">
          {draft}
        </p>
      )}
      <div className="flex gap-2 mt-2">
        <button
          onClick={send}
          disabled={busy || !draft.trim()}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1E293B] text-white rounded-lg px-3 py-1.5 disabled:opacity-50"
        >
          <Send size={12} /> Send text
        </button>
        <button
          onClick={() => setEditing((v) => !v)}
          className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-white"
        >
          {editing ? "Done editing" : "Edit"}
        </button>
        <button
          onClick={dismiss}
          className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
